import { useContext, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import * as React from 'react'

import {
  Authenticated,
  BottomLoadingIndicator,
  ContextualLeftNav,
  Footer,
  Loader,
  NetErrorFeedback,
  SSOIframe,
  TopNav,
} from '@paytrace/pt-skel/Component'
import { doSignOut } from '@paytrace/pt-skel/Component/Helper'
import { AppCtx, AppCtxI, AuthCtx, FormHintCtxProvider } from '@paytrace/pt-skel/Context'
import Persistence from '@paytrace/pt-skel/Context/Persistence'
import * as ST from '@paytrace/pt-skel/type'
import PubSub from '@paytrace/pt-skel/Event/PubSub'
import { useMountUnmount } from '@paytrace/pt-skel/Hooks'

import { ButtonLabelBuilder } from '@paytrace/pt-skel/Helper/ButtonLabelBuilder'
import { addStitchToDocument, ensureStitchForMerchantId } from '@paytrace/pt-skel/Helper/Stitch'
import { SSOCommonParentMessaging } from '@paytrace/pt-skel/Helper/SSOMessaging'
import { configSingleton } from '@paytrace/pt-skel/Config'
import { getActiveMenuSection } from './Helper/util'

import Event from './Event'
import * as T from './types'

interface P {
  children?: React.ReactNode
  section: string
  showBottomLoading?: boolean
  footerIcon?: React.JSX.Element; // for custom footer icon
}

// TODO: Think about making the 'doSignOut' equivalent in the base.
// It handles sso-referrer/bouncing to legacy when appropriate.
// NOTE: This has to swap context beneath the AppCtx, as the base AuthCtx
// derives some value from AppCtx - so this cannot be an outermost nesting,
// but most remain as it is placed currently.
function InjectDynamicLogout (props: { children?: React.ReactNode }) {
  const authContext = useContext(AuthCtx)

  // Overwrite the base AuthCtx.logoutCb with our app level one - this
  // will cause SelfProvider, NoneProvider, and KeycloakProvider to all
  // use the same logout routine, which wouldn't work for the latter 2, but
  // is fine, as the latter 2 are not actually used in any apps atm.
  return (
    <AuthCtx.Provider value={{ ...authContext, logoutCb: doSignOut }}>
      {props.children}
    </AuthCtx.Provider>
  )
}

const mapFeatureFlagsWithMerchantRecord = (
  merchantRecord: ST.MerchantRecord,
  featureFlags: { [key: string]: boolean }
): ST.MerchantRecord => {
  return {
    ...merchantRecord,
    is_edv2_enabled: Boolean(featureFlags?.pt_edapi_edui),
  }
}

const self = Symbol('DynamicPage')

// We do some context setting in here, as DynamicPage is always authenticated,
// while the top level App.tsx is not.
export function DynamicPage (props: P) {
  const context = useContext(AppCtx)
  const { children, showBottomLoading = true, footerIcon } = props

  const ssoIframeRef = useRef<HTMLIFrameElement>(null)
  const bffDomain = configSingleton.env.BFF_DOMAIN || ''

  // State type stuff
  const [hasLoadedAllContext, setHasLoadedAllContext] = useState(false)
  const [merchantLabel, setMerchantLabel] = useState('inner')
  const [usernameLabel, setUsernameLabel] = useState('inner')
  const [merchant, setMerchant] = useState('-1')
  const [user, setUser] = useState<ST.UserRecord>({})
  const [company, setCompany] = useState<ST.CompanyRecord>({})
  const [merchantRecord, setMerchantRecord] = useState<ST.MerchantRecord>({})
  const [associatedMerchantList, setAssociatedMerchantList] = useState<ST.MerchantListRecord[]>([])
  const [ssoIframeIsLoaded, setSsoIframeIsLoaded] = useState(false)
  const [primaryStitchErrored, setPrimaryStitchErrored] = useState(false)
  const [featureFlags, setFeatureFlags] = useState<{ [key: string]: boolean }>({})

  const stateToContext = (oldCtx: AppCtxI): AppCtxI => {
    return {
      ...oldCtx,
      merchantLabel,
      usernameLabel,
      merchant,
      paytraceId: merchant,
      featureFlags,
      records: {
        ...oldCtx.records,
        user,
        company,
        merchant: merchantRecord,
        associatedMerchantList,
        misc: {
          ...oldCtx.records.misc,
          // logged in as reseller or reseller user and a merchant is selected
          is_managing_company: (user.is_reseller && !user.is_admin) && parseInt(merchant) > 0,
          is_merchant_user: (!user.is_reseller && !user.is_admin) && parseInt(merchant) > 0,
        },
      },
    }
  }

  useEffect(() => {
    if (!(ssoIframeIsLoaded && primaryStitchErrored)) {
      return
    }
    if (!ssoIframeRef.current) {
      console.error('Unable to reference SSO Iframe for stitch fallback process')
      return
    }

    console.debug('Attempting to fetch SSO Iframe token for stitch fallback load')
    const tokenResponseHandler = (refreshToken: string) => {
      console.debug('Adding stitch with SSO Iframe token')
      addStitchToDocument(merchant, refreshToken)
    }
    const sso = new SSOCommonParentMessaging(ssoIframeRef.current, bffDomain, window, tokenResponseHandler)
    sso.getRefreshToken()
  }, [ssoIframeIsLoaded, primaryStitchErrored])

  useMountUnmount(() => {
    PubSub.sub(
      self,
      Event.SET_CTX_USER,
      ({ userRecord, merchantList, fullMerchantList, featureFlags }: T.CtxUserRes) => {
        // Resume merchant id that was loaded from another site/context
        if (Persistence.getItem('current-merchant-id')) {
          const merchant_id = Persistence.getItem('current-merchant-id')

          // All that matters is the 0th merchant is the one we want
          // So just remove all the other ones from the list
          merchantList = merchantList.filter((merchant: { merchant_id: string }) => {
            return Number(merchant.merchant_id) === Number(merchant_id)
          })
        }
        // FIXME: Some type of error here, indicating the ID was bad/not in their list
        // They should never legitimately hit this (only via URL manipulation)

        const buttonLabelBuilder = new ButtonLabelBuilder(userRecord, merchantList)
        const merchantId: string = String(merchantList[0].merchant_id)

        setMerchantLabel(buttonLabelBuilder.makeMerchantLabel())
        setUsernameLabel(buttonLabelBuilder.makeUserLabel())
        setMerchant(merchantId)
        setHasLoadedAllContext(true)
        setUser(userRecord)
        // Company is a synonym for "Merchant", except when running in "Reseller" mode
        // in which case, it is then the reseller record and not the merchant record.
        setCompany(merchantList[0])
        setMerchantRecord(
          mapFeatureFlagsWithMerchantRecord(merchantList[0], featureFlags)
        )
        setAssociatedMerchantList(fullMerchantList)
        setFeatureFlags(featureFlags)
        const handleStitchError = () => setPrimaryStitchErrored(true)
        ensureStitchForMerchantId(merchantId, handleStitchError)

        // Snapshot it to avoid unneccessary reloads of this data
        Persistence.setItem('CTX_USER', { userRecord, merchantList, fullMerchantList, featureFlags })
      }
    )

    if (Persistence.getItem('CTX_USER')) {
      PubSub.pub(Event.SET_CTX_USER, Persistence.getItem('CTX_USER'))
    } else {
      PubSub.pub(Event.GET_CTX_USER)
    }

    return () => {
      PubSub.unsub(self)
    }
  })

  const location = useLocation()

  // Do not show some stuff like left/top navs while loading user data, as
  // these need the proper user context.
  // If a full screen "Initializing/loader" is too much, we could pinpoint
  // just those elements in the TopNav perhaps, so the user time to
  // first paint/being able to navigate the site is very minimal (however it'll
  // be unusable until those things load anyways, just has to do with perception...)
  if (!hasLoadedAllContext) {
    return (
      <Authenticated>
        <main className='main-wrapper'>
          <section className='dyn-page'>
            <h1>Initializing...</h1>
            <NetErrorFeedback />
            <Loader style={{ marginTop: 100 }} />
          </section>
        </main>
      </Authenticated>
    )
  }

  return (
    <Authenticated>
      <AppCtx.Provider value={stateToContext(context)}>
        <InjectDynamicLogout>
          <FormHintCtxProvider>
            <TopNav />
            <NetErrorFeedback />
            <main className='main-wrapper'>
              {/* This should be the system/front end, as each is distinct */}
              <ContextualLeftNav
                initialItem={
                  getActiveMenuSection(location.pathname)
                }
              />
              <section key={location.key} className='dyn-page'>
                {children || <React.Fragment />}
              </section>
            </main>
            <Footer icon={footerIcon} />
            {showBottomLoading && <BottomLoadingIndicator />}
            <SSOIframe ref={ssoIframeRef} onload={() => setSsoIframeIsLoaded(true)}/>
          </FormHintCtxProvider>
        </InjectDynamicLogout>
      </AppCtx.Provider>
    </Authenticated>
  )
}

export default DynamicPage
